import { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  Button,
  SpaceBetween,
  Tabs,
  Box,
  Header,
  Table as CSDTable,
} from "@cloudscape-design/components-themed";
import { DEFAULT_PAGE_SIZE, USER_TYPE } from "../../configs/constants";
import Preference from "../UI/Preference";
import Pagination from "../UI/Pagination";
import { getHeaderCounterText, getPagedItems } from "../../utils/common";
import { COMMON_LABELS } from "../default-constants";
import { LABELS } from "./constants";
import { GatewayContract } from "../../features/api-interfaces";
import {
  getStatusLoc,
  getSettingValueLoc,
  isLink,
  isLinkIpidValue,
} from "./util";
import { useCanIShowHook } from "../../app/useCanIShowHook";

const columnDefinitions = (isDocomoUser: boolean) => {
  return [
    {
      id: "gwName",
      header: LABELS.CORP_TABLE_CORPNAME,
      cell: (e: any) =>
        // GW契約内容登録・更新
        isDocomoUser ? (
          <Link
            to={
              "/gateway-companies-contracts/" +
              e.gwCompanyCode +
              "/" +
              e.gatewayNo
            }
          >
            {e.gwName}
          </Link>
        ) : (
          e.gwName
        ),
    },
    {
      id: "contractServiceName",
      header: LABELS.CORP_TABLE_SERVICE,
      cell: (e: any) => e.contractServiceName,
    },
    {
      id: "contractStatus",
      header: LABELS.CORP_TABLE_STATUS,
      cell: (e: any) =>
        e.contractStoppedFlag
          ? LABELS.CONTRACT_STATUS_STOPPED
          : LABELS.CONTRACT_STATUS_NOT_STOPPED,
    },
    {
      id: "connection",
      header: LABELS.CORP_TABLE_CONNECTION_ENVIRONMENTAL,
      cell: (e: any) =>
        isLink(e.connectionEnvApprovalStatus, isDocomoUser) ? (
          // TODO URL
          <Link to="#">{getStatusLoc(e.connectionEnvApprovalStatus)}</Link>
        ) : (
          getStatusLoc(e.connectionEnvApprovalStatus)
        ),
    },
    {
      id: "implement",
      header: LABELS.CORP_TABLE_IMPLEMENTATION_CONFIRMATION,
      cell: (e: any) =>
        isLink(e.impleConfirmationApprovalStatus, isDocomoUser) ? (
          // TODO URL
          <Link to="#">{getStatusLoc(e.impleConfirmationApprovalStatus)}</Link>
        ) : (
          getStatusLoc(e.impleConfirmationApprovalStatus)
        ),
    },
    {
      id: "ipid",
      header: LABELS.CORP_TABLE_IPID_PRIOR_PAYOUT,
      cell: (e: any) =>
        isLinkIpidValue(e.priorPayoutIpidRuleSettingStatus) ? (
          // TODO URL
          <Link to="#">
            {getSettingValueLoc(e.priorPayoutIpidRuleSettingStatus)}
          </Link>
        ) : (
          getSettingValueLoc(e.priorPayoutIpidRuleSettingStatus)
        ),
    },
  ];
};

export const Tab = (props: any) => {
  // 事業者種別取得
  const isDocomoUser = useCanIShowHook(USER_TYPE.DOCOMO);

  const navigate = useNavigate();
  const { code } = useParams();

  // データをセット
  const gatewayContractList: GatewayContract[] =
    props.gatewayCompanyContractData;

  // 事業者コード
  const [gwCompanyCode] = useState(code ? code : "");

  /**ページャ関連 */
  const options = columnDefinitions(isDocomoUser).map((column) => ({
    id: column.id,
    label: column.header,
  }));
  const [currentPageIndex, setCurrentPageIndex] = useState(1);
  const [preferences, setPreferences] = useState({
    pageSize: DEFAULT_PAGE_SIZE,
    visibleContent: options.map((o) => o.id),
  });

  const [pagedItems, setPagedItems] = useState<Object[]>([]);
  const [allItems, setAllItems] = useState<Object[]>([]);
  useEffect(() => {
    if (gatewayContractList !== undefined) {
      setPagedItems(getPagedItems(gatewayContractList, 1, DEFAULT_PAGE_SIZE));
      setAllItems(gatewayContractList);
      setCurrentPageIndex(1);
    }
  }, [gatewayContractList]);

  /** 設定変更関数 */
  const handleOnConfirm = (detail: any) => {
    const newPageSize = detail.pageSize;
    setPreferences({
      pageSize: newPageSize,
      visibleContent: detail.visibleContent,
    });
    setPagedItems(gatewayContractList.slice(0, newPageSize));
    setCurrentPageIndex(1);
  };

  const visibleColumnList = isDocomoUser
    ? [
        "gwName",
        "contractServiceName",
        "contractStatus",
        "connection",
        "implement",
        "ipid",
      ]
    : ["gwName", "contractServiceName", "connection", "implement"];

  return (
    <Tabs
      variant="container"
      tabs={[
        {
          label: LABELS.TAB_TITLE_GW_LIST,
          id: "first",
          content: (
            <CSDTable
              columnDefinitions={columnDefinitions(isDocomoUser)}
              items={pagedItems}
              loading={props.isFetching}
              loadingText={COMMON_LABELS.CSD.LOADING_TEXT}
              resizableColumns
              stickyHeader
              header={
                <SpaceBetween size="xs">
                  <SpaceBetween direction="horizontal" size="l">
                    {isDocomoUser && (
                      <Button
                        onClick={() => {
                          navigate(
                            "/gateway-companies-contracts/" + gwCompanyCode
                          );
                        }}
                      >
                        {LABELS.CORP_GW_TITLE}
                      </Button>
                    )}
                  </SpaceBetween>
                  <Header>
                    {LABELS.TAB_TITLE}
                    {getHeaderCounterText(allItems.length, pagedItems.length)}
                  </Header>
                </SpaceBetween>
              }
              visibleColumns={visibleColumnList}
              pagination={
                <Pagination
                  currentPageIndex={currentPageIndex}
                  filteredItems={allItems}
                  setCurrentPageIndex={setCurrentPageIndex}
                  pageSize={preferences.pageSize}
                  setPagedItems={setPagedItems}
                />
              }
              preferences={
                <Preference
                  preferences={preferences}
                  options={options}
                  handleOnConfirm={handleOnConfirm}
                />
              }
              empty={
                <Box textAlign="center" color="inherit">
                  {COMMON_LABELS.CSD.NO_DATA}
                </Box>
              }
            ></CSDTable>
          ),
        },
      ]}
    />
  );
};
